<template>
  <div class="component stk-p-10">
    <div>
      <router-link 
        :to="{ path: `/advisor/entity/${entityId}/documents` }" 
        class="stk-button stk-size-3"
      >
        <span class="stk-icon"><i class="fas fa-chevron-left"></i></span>
        <span> Back to Documents</span>
      </router-link>
    </div>

    <Level class="stk-py-6">
      <LevelLeft>
        <Stack gap="2">
          <Title>
            {{ actualFilename || 'Document' }}
          </Title>

          <Badges v-if="document.classification || formattedDocDate">
            <Badge v-if="document.classification">{{ document.classification|| '' }}</Badge>
            <Badge v-if="formattedDocDate">{{ formattedDocDate }}</Badge> 
          </Badges>
        </Stack>
      </LevelLeft>

      <LevelRight v-if="validationRules.length">
        <Button
          @click="toggleValidationDrawer" 
          size="3"
          :variant="failedValidations.length > 0 ? 'danger' : 'success'"
          :iconBefore="failedValidations.length > 0 ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'"
        >
          <span>{{ showValidationDrawer ? 'Hide' : 'Show' }} DocCheckup</span>
        </Button>
      </LevelRight>
    </Level>

    <!-- Main Content -->
    <div v-if="loading">
      <LoaderWithText>
        Fetching Document…
      </LoaderWithText>
    </div>

    <template v-else>
      <div class="action-bar stk-mb-4">
        <Pills>
          <PillsList>
            <PillItem
              @click="activeTab = 'details'"
              :active="activeTab === 'details'"
              icon="fas fa-list"
            >
              Extracted Information
            </PillItem>
          </PillsList>
        </Pills>
      </div>

      <div class="content-with-drawer">
        <div class="main-content" :class="{'with-drawer': showValidationDrawer}">
          <div v-if="activeTab === 'details'">
            <DocumentDetails
              :extract-info="document.extract_info || []"
              :classification="document.classification || ''"
              :document-url="document.download_url"
              :document-id="document.external_id"
              @validation-updated="refreshDocumentData"
            />
          </div>

          <div v-else-if="activeTab === 'docusign'" class="docusign-content">
            <div class="columns">
              <div class="column is-one-third">
                <div class="box">
                  <div v-if="!docuSignConnected">
                    <p>Connect to DocuSign to proceed</p>
                    <button class="button is-info" @click="initiateDocuSignAuth">
                      Connect to DocuSign
                    </button>
                  </div>
                  <div v-else>
                    <div class="field">
                      <label class="label">Select Template</label>
                      <div class="control">
                        <div class="select is-fullwidth">
                          <select v-model="selectedTemplate" @change="loadTemplateDocuments">
                            <option value="">Select a template</option>
                            <option v-for="template in docuSignTemplates" :key="template.id" :value="template.id">
                              {{ template.name }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div v-if="templateDocuments.length > 0" class="field">
                      <label class="label">Select Document</label>
                      <div class="control">
                        <div class="select is-fullwidth">
                          <select v-model="selectedDocumentId" @change="loadTemplateDocument">
                            <option value="">Select a document</option>
                            <option v-for="doc in templateDocuments" :key="doc.document_id" :value="doc.document_id">
                              {{ doc.name }}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div class="buttons">
                      <button 
                        class="button is-primary" 
                        @click="autofillForm" 
                        :disabled="!selectedTemplate || isAutofilling"
                      >
                        <span class="icon" v-if="isAutofilling">
                          <i class="fas fa-spinner fa-spin"></i>
                        </span>
                        <span>{{ isAutofilling ? 'Autofilling...' : 'Autofill Form' }}</span>
                      </button>
                      <button 
                        class="button is-success" 
                        @click="createEnvelope" 
                        :disabled="!selectedTemplate || isCreatingEnvelope"
                      >
                        <span class="icon" v-if="isCreatingEnvelope">
                          <i class="fas fa-spinner fa-spin"></i>
                        </span>
                        <span>{{ isCreatingEnvelope ? 'Creating...' : 'Create Agreement' }}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="column is-two-thirds">
                <div class="pdf-container">
                  <div v-if="currentPdfUrl">
                    <div class="page-navigation mb-4">
                      <button 
                        class="button is-small"
                        :disabled="currentPdfPage <= 1"
                        @click="currentPdfPage--"
                      >
                        <span class="icon">
                          <i class="fas fa-chevron-left"></i>
                        </span>
                      </button>
                      
                      <div class="page-input mx-2">
                        <input
                          v-model.number="currentPdfPage"
                          type="number"
                          min="1"
                          :max="totalPages"
                          class="input is-small"
                          style="width: 70px"
                        >
                        <span class="ml-2" v-if="totalPages">of {{ totalPages }}</span>
                      </div>

                      <button 
                        class="button is-small"
                        :disabled="currentPdfPage >= totalPages"
                        @click="currentPdfPage++"
                      >
                        <span class="icon">
                          <i class="fas fa-chevron-right"></i>
                        </span>
                      </button>
                    </div>

                    <div class="pdf-viewer">
                      <div class="canvas-container">
                        <canvas ref="pdfCanvas"></canvas>
                        <canvas ref="highlightCanvas" class="highlight-canvas"></canvas>
                        <canvas ref="formCanvas" class="form-canvas"></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <transition name="drawer">
          <div v-if="showValidationDrawer" class="validation-drawer">
            <div class="drawer-header">
              <Stack gap="1">
                <Title>Document Checkup</Title>
                <div class="validation-summary">
                  <Badge 
                    :variant="failedValidations.length > 0 ? 'danger' : 'success'"
                  >
                    {{ passedValidations.length }} of {{ validationRules.length }} checks passed
                  </Badge>
                </div>
              </Stack>
              <Button 
                size="3" 
                variant="text" 
                @click="toggleValidationDrawer" 
                iconBefore="fas fa-times"
              />
            </div>
            
            <ValidationDetails 
              :validation-rules="validationRules" 
              :document-type="document.classification"
              :full-classification="document.full_classification"
              :document-id="document.external_id"
              @validation-deleted="refreshValidationData"
            />
          </div>
        </transition>
      </div>
    </template>
  </div>
</template>

<script>
import DocumentDetails from '../components/DocumentDetails.vue';
import ValidationDetails from '../components/ValidationDetails.vue';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Badge,
  Badges,
  Buttons,
  Button,
  Level,
  LevelLeft,
  LevelRight,
  LoaderWithText,
  Pills,
  PillsList,
  PillItem,
  Stack,
  Title,
  Subtitle,
} from '@stakeui';

// Use webpack's publicPath which is correctly configured for both dev and prod
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs';
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(workerUrl, window.location.origin).href;
const pdfWorker = new pdfjsLib.PDFWorker();

export default {
  name: 'DocumentView',
  components: {
    DocumentDetails,
    ValidationDetails,
    Badge,
    Badges,
    Buttons,
    Button,
    Level,
    LevelLeft,
    LevelRight,
    LoaderWithText,
    Pills,
    PillsList,
    PillItem,
    Stack,
    Title,
    Subtitle,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    entityId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      document: {},
      documentDetails: {},
      hasPreviousDocument: false,
      hasNextDocument: false,
      activeTab: 'details',
      validationRules: [],
      docuSignConnected: false,
      docuSignTemplates: [],
      selectedTemplate: '',
      selectedDocumentId: '',
      templateDocuments: [],
      templateFields: [],
      currentPdfUrl: null,
      currentPdfPage: 1,
      totalPages: 0,
      pdfDoc: null,
      pdfPage: null,
      pdfScale: 1.5,
      isAutofilling: false,
      isCreatingEnvelope: false,
      showValidationDrawer: true,
    };
  },
  async mounted() {
    try {
      await this.fetchDocument();
      await this.checkDocuSignConnection();
    } finally {
      this.loading = false;
    }
  },
  computed: {
    actualFilename() {
      return this.document?.filename ?
        this.document.filename.split('_').slice(2).join('_') :
        'Document';
    },
    formattedDocDate() {
      if (this.document.filename) {
        const datePart = this.document.filename.split('_')[0];
        const date = new Date(datePart);
        if (!isNaN(date)) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        }
      }
      return '';
    },
    passedValidations() {
      return this.validationRules.filter(rule => rule.passed);
    },
    failedValidations() {
      return this.validationRules.filter(rule => !rule.passed);
    },
    validationStatusClass() {
      return {
        'has-background-success': this.failedValidations.length === 0,
        'has-background-danger': this.failedValidations.length > 0,
        'has-text-white': true,
        'is-size-7': true,
        'px-2': true,
        'py-1': true,
        'rounded': true,
      };
    },
  },
  methods: {
    formatRuleName(name) {
      if (!name) return '';
      return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },
    async fetchDocument() {
      try {
        const response = await fetch(`/api/v1/docupload/${this.id}/`, {
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
        });
        const data = await response.json();
        this.document = data.document;
        if (this.document.external_id) {
          await this.loadValidationStatus();
        }
      } catch (error) {
        console.error('Error fetching document:', error);
      }
    },
    async checkDocuSignConnection() {
      try {
        const response = await fetch('/api/v1/docusign/status');
        if (response.ok) {
          const data = await response.json();
          this.docuSignConnected = data.connected;
          if (this.docuSignConnected) {
            await this.fetchDocuSignTemplates();
          }
        }
      } catch (error) {
        console.error('Error checking DocuSign connection:', error);
      }
    },
    async fetchDocuSignTemplates() {
      try {
        const response = await fetch('/api/v1/docusign/templates');
        if (response.ok) {
          this.docuSignTemplates = await response.json();
        }
      } catch (error) {
        console.error('Error fetching DocuSign templates:', error);
      }
    },
    async loadTemplateDocuments() {
      if (!this.selectedTemplate) return;
      try {
        const response = await fetch(`/api/v1/docusign/templates/${this.selectedTemplate}/documents/`);
        if (response.ok) {
          this.templateDocuments = await response.json();
          this.selectedDocumentId = '';
          await this.fetchTemplateFields();
        }
      } catch (error) {
        console.error('Error loading template documents:', error);
      }
    },
    async loadTemplateDocument() {
      if (!this.selectedTemplate || !this.selectedDocumentId) return;
      try {
        const response = await fetch(`/api/v1/docusign/templates/${this.selectedTemplate}/documents/${this.selectedDocumentId}/`);
        if (response.ok) {
          const blob = await response.blob();
          this.currentPdfUrl = URL.createObjectURL(blob);
          await this.renderPage(1);
        }
      } catch (error) {
        console.error('Error loading template document:', error);
      }
    },
    async fetchTemplateFields() {
      try {
        const response = await fetch(`/api/v1/docusign/template-fields?template_id=${this.selectedTemplate}`);
        if (response.ok) {
          this.templateFields = await response.json();
        }
      } catch (error) {
        console.error('Error fetching template fields:', error);
      }
    },
    async renderPage(pageNumber) {
      if (!this.pdfDoc) {
        try {
          this.pdfDoc = await pdfjsLib.getDocument({
            url: this.currentPdfUrl,
            isEvalSupported: false,
            worker: pdfWorker,
          }).promise;
          this.totalPages = this.pdfDoc.numPages;
        } catch (error) {
          console.error('Error loading PDF:', error);
          return;
        }
      }

      try {
        this.pdfPage = await this.pdfDoc.getPage(pageNumber);
        const pdfCanvas = this.$refs.pdfCanvas;
        const formCanvas = this.$refs.formCanvas;
        const pdfContext = pdfCanvas.getContext('2d');
        const formContext = formCanvas.getContext('2d');
        const viewport = this.pdfPage.getViewport({ scale: this.pdfScale });

        // Set canvas dimensions
        pdfCanvas.height = viewport.height;
        pdfCanvas.width = viewport.width;
        formCanvas.height = viewport.height;
        formCanvas.width = viewport.width;

        // Clear canvases
        pdfContext.clearRect(0, 0, pdfCanvas.width, pdfCanvas.height);
        formContext.clearRect(0, 0, formCanvas.width, formCanvas.height);

        // Render PDF
        await this.pdfPage.render({
          canvasContext: pdfContext,
          viewport: viewport,
        }).promise;

        // Draw form fields for current page
        this.drawFormFields(formContext, viewport, pageNumber);
      } catch (error) {
        console.error('Error rendering PDF page:', error);
      }
    },
    drawFormFields(ctx, viewport, pageNumber) {
      const fields = this.templateFields.filter(field => field.page_number === pageNumber);
      fields.forEach(field => {
        const x = field.x_position * viewport.width;
        const y = field.y_position * viewport.height;
        ctx.fillStyle = 'rgba(135, 206, 250, 0.3)';
        ctx.strokeStyle = '#4a90e2';
        ctx.lineWidth = 1;
        // Draw field box
        ctx.fillRect(x, y, 100, 20); // Adjust size as needed
        ctx.strokeRect(x, y, 100, 20);
        // Draw field label
        ctx.fillStyle = '#4a90e2';
        ctx.font = '12px Arial';
        ctx.fillText(field.name, x, y - 5);
      });
    },
    async initiateDocuSignAuth() {
      try {
        const response = await fetch('/api/v1/docusign/auth_url');
        if (response.ok) {
          const data = await response.json();
          const authWindow = window.open(data.auth_url, 'DocuSign Auth', 'width=800,height=600');
          
          window.addEventListener('message', async (event) => {
            if (event.data.type === 'DOCUSIGN_AUTH_CALLBACK') {
              authWindow.close();
              if (event.data.success) {
                await this.checkDocuSignConnection();
              }
            }
          });
        }
      } catch (error) {
        console.error('Error initiating DocuSign auth:', error);
      }
    },
    async autofillForm() {
      this.isAutofilling = true;
      try {
        const response = await fetch('/api/v1/docusign/autofill-form/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            customFields: this.templateFields,
            clientId: this.entityId,
            useDossier: false
          }),
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update template fields with autofilled values
          this.templateFields = this.templateFields.map(field => {
            const autofilled = data.fields.find(f => f.name === field.name);
            if (autofilled) {
              return { ...field, value: autofilled.value };
            }
            return field;
          });
          
          // Re-render current page to show autofilled values
          await this.renderPage(this.currentPdfPage);
        }
      } catch (error) {
        console.error('Error autofilling form:', error);
      } finally {
        this.isAutofilling = false;
      }
    },
    async createEnvelope() {
      this.isCreatingEnvelope = true;
      try {
        const formattedFields = this.templateFields
          .filter(field => field.value)
          .map(field => ({
            name: field.name,
            value: field.value,
          }));

        const response = await fetch('/api/v1/docusign/create-envelope/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            templateId: this.selectedTemplate,
            fields: formattedFields,
          }),
        });

        const data = await response.json();
        if (response.ok) {
          // Open DocuSign envelope in new window
          window.open(data.view_url, '_blank');
        }
      } catch (error) {
        console.error('Error creating envelope:', error);
      } finally {
        this.isCreatingEnvelope = false;
      }
    },
    async refreshDocumentData() {
      console.log('Refreshing document data...');
      this.loading = true; // in case later we want to show this as in progress in UI
      try {
        // Refresh both the document (extract_info) and validation results
        await this.fetchDocument();
        await this.loadValidationStatus();
        console.log('Validation data refreshed, rules count:', this.validationRules.length);
        console.log('Updated validation rules:', this.validationRules);
    
      } finally {
        this.loading = false;
      }
    },
    async refreshValidationData() {
      console.log('Refreshing validation data...');
      this.loading = true; // in case later we want to show this as in progress in UI
      try {
        // Refreshvalidation results
        await this.loadValidationStatus();
        console.log('Validation data refreshed, rules count:', this.validationRules.length);
        console.log('Updated validation rules:', this.validationRules);
    
      } finally {
        this.loading = false;
      }
    },
    async loadValidationStatus() {
      try {
        // Fetch validation results using the document's external ID
        const response = await fetch(`/api/v1/documents/${this.document.external_id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to load validation results');
        }

        const result = await response.json();
        console.log('VALIDATION FETCHED FOR CURRENT DOCUMENT', result.document[0].validation);

        // Check if validation exists and is an array
        // TODO: Uncomment below when we want to show validation status for all documents again
        if (Array.isArray(result.document[0].validation)) {
        // if (Array.isArray(result.document[0].validation) && 
        //   result.document[0].classification === 'Brokerage Account Statement'){ // temporary while validation checks are improved
          this.validationRules = result.document[0].validation.map((rule, index) => ({
            id: `${rule.name}-${rule.start_page}-${rule.end_page}-${index}`, // Include page info for uniqueness
            name: this.formatRuleName(rule.name) || 'Unnamed Validation',
            description: rule.description || this.formatRuleName(rule.name) || 'Unnamed Validation',
            document_type: rule.document_type || result.document[0].classification?.document_type || result.document[0].classification?.classification || '',
            passed: rule.passed || false,
            details: rule.details || null,
            start_page: rule.start_page || null,
            end_page: rule.end_page || null,
          }));
        } else {
          // If no validation exists, initialize as an empty array
          this.validationRules = [];
        }
      } catch (error) {
        console.error('Error loading validation results:', error.message);
        alert('Failed to load validation results. Please try again.');
        this.validationRules = []; // Reset validation rules on error
      }
      
      // After loading validation rules, ensure drawer is open if there are rules
      if (this.validationRules.length > 0) {
        this.showValidationDrawer = true;
      }
    },

    handleDownload() {
      window.open(`/api/v1/docupload/${this.id}/file/`, '_blank');
    },
    handleShare() {
      // Implement share functionality
      console.log('Share document:', this.id);
    },
    navigateDocument(direction) {
      // Implement navigation functionality
      console.log('Navigate', direction);
    },
    formatLabel(key) {
      return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    },
    scrollToValidationRule(rule) {
      // No need to toggle drawer as it's already open
      // Just scroll to the specific rule
      this.$nextTick(() => {
        const element = document.getElementById(`validation-rule-${rule.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          element.classList.add('highlight-rule');
          setTimeout(() => {
            element.classList.remove('highlight-rule');
          }, 2000);
        }
      });
    },
    toggleValidationDrawer() {
      this.showValidationDrawer = !this.showValidationDrawer;
    },
  },
};
</script>

<style scoped>
.has-text-weight-extra-bold {
  font-weight: 900 !important;
}

.custom-tabs-container {
  display: inline-flex;
  background-color: #f3f4f6;
  padding: 0.25rem;
  border-radius: 9999px;
}

.custom-tabs :deep(.o-tabs-wrapper) {
  background-color: transparent;
}

.custom-tabs :deep(.o-tabs-item) {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  border-radius: 9999px;
  transition: all 0.3s ease-in-out;
  padding: 0.25rem 0.75rem;
}

.custom-tabs :deep(.o-tabs-item.is-active) {
  background-color: white;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.custom-tabs :deep(.o-tabs-item .tab-label) {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}

.custom-tabs :deep(.o-tabs-item.is-active .tab-label) {
  color: #2563eb;
}

@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: 1fr;
  }

  .sidebar {
    margin-top: 1rem;
  }
}

.pdf-container {
  position: relative;
  width: 100%;
  height: 800px;
  background: white;
  border-radius: 4px;
}

.pdf-container :deep(iframe),
.pdf-container :deep(embed) {
  width: 100%;
  height: 100%;
  border: none;
}

.validation-rule-item {
  border-radius: 8px;
  border: 1px solid #eee;
}

.validation-rule-item:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}


.tabs-wrapper {
  border-bottom: 1px solid #eee;
}


.validation-progress {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.validation-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.validation-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.validation-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.validation-chip.is-success {
  background-color: #f0fdf4;
  color: #166534;
  border: 1px solid #dcfce7;
}

.validation-chip.is-danger {
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fee2e2;
}

.validation-chip-label {
  margin-left: 0.5rem;
}

.highlight-rule {
  animation: highlight 2s ease-out;
}

@keyframes highlight {
  0% {
    background-color: #fff9c4;
  }
  100% {
    background-color: transparent;
  }
}

/* Make the validation chips responsive */
@media (max-width: 768px) {
  .validation-chips {
    flex-direction: column;
  }
  
  .validation-chip {
    width: 100%;
  }
}

.docusign-content {
  margin-top: 1rem;
}

.canvas-container {
  position: relative;
  width: 100%;
  overflow: auto;
}

.form-canvas {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.pdf-container {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
}

.page-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.page-input {
  display: flex;
  align-items: center;
  margin: 0 1rem;
}

.validation-status-indicator {
  border-radius: 9999px;
  padding: 0.25rem 0.5rem;
}

/* New styles for the drawer layout */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.content-with-drawer {
  display: flex;
  position: relative;
  min-height: 500px;
}

.main-content {
  flex: 1;
  transition: width 0.3s ease;
  width: 100%;
}

.main-content.with-drawer {
  width: calc(100% - 350px);
}

.validation-drawer {
  width: 350px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  background: white;
  border-left: 1px solid #e5e7eb;
  box-shadow: -4px 0 6px -1px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  overflow-y: auto;
  z-index: 10;
  height: 100%;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}

/* Drawer animation */
.drawer-enter-active, .drawer-leave-active {
  transition: transform 0.3s ease;
}

.drawer-enter-from, .drawer-leave-to {
  transform: translateX(100%);
}

/* Make drawer full-width on mobile */
@media (max-width: 768px) {
  .main-content.with-drawer {
    width: 0;
    overflow: hidden;
  }
  
  .validation-drawer {
    width: 100%;
  }
}

/* New styles for validation summary in drawer */
.validation-summary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.validation-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--stk-border-color);
}
</style>
